import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="12857" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted/20 animate-pulse rounded-none" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-mono font-medium text-[#F5F5DC]/80 transition-colors hover:text-[#F5F5DC] focus:outline-none uppercase tracking-wider">
                {item.label}
                {}
                <span className="absolute -bottom-1 left-0 h-[2px] w-full scale-x-0 rounded-none bg-primary transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100 origin-left" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-6 text-sm font-mono text-muted-foreground uppercase tracking-widest">Synchronizing...</div>;
  }
  return <nav className="flex flex-col space-y-6 p-6 bg-[#003153]">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-serif font-bold text-[#F5F5DC] transition-colors hover:text-primary uppercase tracking-tighter" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="pt-6 flex flex-col gap-4 border-t border-primary/20">
        <Link to="/CommandCenterPage" onClick={onClose} className="text-lg font-mono font-medium text-[#F5F5DC]/70 hover:text-primary uppercase tracking-widest flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
          System Status: Active
        </Link>
        <Button className="w-full rounded-none bg-primary text-lg font-serif font-black text-primary-foreground hover:bg-primary/90 h-14 uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]" asChild>
          <Link to="/OperationalAuditPage" onClick={onClose}>
            Begin Audit
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function EduSproutHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="operational-header" className={`dark sticky top-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-2xl border-primary/20" : "bg-[#003153] border-transparent"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 outline-none">
            <div className="logo-area flex items-center gap-2">
              <img src="https://neutral-cowbird.10web.cloud/wp-content/uploads/2026/03/Screenshot-2026-03-27-at-15.37.10.png" alt="Exergy Ops Icon" className="h-12 w-auto object-contain" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/exergy_ops_bold_logo.png" alt="Exergy Ops Logo" className="h-8 w-auto object-contain" />
            </div>
          </Link>
        </div>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16" className="w-full">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden items-center gap-8 md:flex">
          <Link to="/CommandCenterPage" className="group flex items-center gap-2 text-[10px] font-mono font-bold text-[#F5F5DC]/60 transition-colors hover:text-primary uppercase tracking-[0.2em]">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            System Status: Active
          </Link>
          <Button asChild className="rounded-none bg-primary px-8 py-6 font-serif font-black text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,0.4)] transition-all hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,0.4)] hover:bg-primary/90 uppercase tracking-widest active:translate-y-0.5 active:shadow-none">
            <Link to="/OperationalAuditPage">Begin Audit</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" className="text-[#F5F5DC] hover:bg-primary/10 rounded-none" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col bg-[#003153] md:hidden animate-in slide-in-from-right-full duration-300">
          <div className="flex h-20 items-center justify-between px-4 border-b border-primary/20">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/exergy_ops_bold_logo.png" alt="Exergy Ops Logo" className="h-8 w-auto" />
            </Link>
            <Button variant="ghost" size="icon" className="text-[#F5F5DC] hover:bg-primary/10 rounded-none" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="16">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}